import { Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import CorrectIcon from "../../resources/icons/succuss-tick.svg";
import PendingIcon from "../../resources/icons/alert-triangle.svg";

const QA_STATUS_VARIANTS = {
  verified: {
    containerClassName: "bg-green-200",
    textClassName: "text-cfIssuedCredentialTemplateCardStatusDoneBG",
    labelKey: "workspace:verified",
    icon: CorrectIcon,
  },
  notVerified: {
    containerClassName: "bg-red-200",
    textClassName: "text-cfIssuedCredentialTemplateCardStatusFailedBG",
    labelKey: "workspace:not_verified",
    actionLabelKey: "workspace:re_verify",
    icon: PendingIcon,
    hoverClassName: "hover:bg-red-300",
  },
  pending: {
    containerClassName: "bg-amber-100",
    textClassName: "text-amber-600",
    labelKey: "workspace:pending",
    actionLabelKey: "workspace:verify",
    icon: PendingIcon,
    hoverClassName: "hover:bg-amber-200",
  },
};

const normalizeQaStatus = (status) =>
  typeof status === "string" ? status.trim().toLowerCase() : "";

const getVariant = (qaTested) => {
  const normalized = normalizeQaStatus(qaTested);
  if (normalized === "verified") return QA_STATUS_VARIANTS.verified;
  if (normalized === "failed") return QA_STATUS_VARIANTS.notVerified;
  return QA_STATUS_VARIANTS.pending;
};

export default function QaTestedBadge({
  qaTested,
  onClick,
  isLoading = false,
  className = "",
}) {
  const { t } = useTranslation();
  const variant = getVariant(qaTested);
  const isVerified = normalizeQaStatus(qaTested) === "verified";
  const isInProgress = normalizeQaStatus(qaTested) === "in-progress";
  const showLoading = isLoading || isInProgress;
  const isInteractive =
    !isVerified && !isInProgress && typeof onClick === "function";

  const label = showLoading
    ? t("workspace:verifying")
    : isInteractive
      ? t(variant.actionLabelKey)
      : t(variant.labelKey);

  const content = (
    <>
      {showLoading ? (
        <Loader2 className={`h-4 w-4 animate-spin ${variant.textClassName}`} />
      ) : (
        <img src={variant.icon} className="h-4 w-4" alt="" />
      )}
      <span className={`font-medium ${variant.textClassName} text-xs`}>
        {label}
      </span>
    </>
  );

  const baseClassName = [
    "flex items-center justify-center gap-2 px-2 py-1 rounded-xl mx-auto w-fit",
    variant.containerClassName,
    className,
  ]
    .filter(Boolean)
    .join(" ");

  if (isInteractive) {
    return (
      <button
        type="button"
        onClick={onClick}
        disabled={showLoading}
        className={`${baseClassName} transition-colors duration-200 ${
          showLoading
            ? "cursor-wait opacity-70"
            : `cursor-pointer ${variant.hoverClassName || ""}`
        }`}
      >
        {content}
      </button>
    );
  }

  return <div className={baseClassName}>{content}</div>;
}
